import { FloraButton, FloraButtonProps, forwardRef, styled } from '@grupoboticario/flora-react'
import { ChevronLeftIcon, ChevronRightIcon } from '@grupoboticario/flora-react-icons'
import React, { MouseEvent, useCallback, useContext, useMemo } from 'react'
import { CarouselContext } from '../Carousel'

const CustomText = styled('span', {
  display: 'inline-block',
  opacity: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  width: '0%',
})
CustomText.toString = () => `.${CustomText.className}`

export const CustomButtom = styled((props: FloraButtonProps) => <FloraButton {...props} />, {
  display: 'inline-flex !important',
  height: '$6 !important',
  position: 'absolute',
  paddingBottom: '0 !important',
  paddingTop: '0 !important',
  transition: 'width 300ms ease-out',
  width: '$6 !important',
  zIndex: '$overlay',
  padding: '0 !important',
  top: '50%',

  '@mobile': {
    width: '$10 !important',
    height: '$10 !important',
    padding: '$4 !important',

    '&:hover': {
      width: '108px !important',

      [`${CustomText.toString()}`]: {
        opacity: 1,
        transition: 'width 300ms ease-out, opacity 300ms ease-in',
        width: '100%',
      },
    },
  },

  '&::before': {
    content: '',
    height: '100%',
    left: ' 50%',
    minHeight: '$11',
    minWidth: '$11',
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    top: '50%',
    width: '100%',
  },
})
CustomButtom.toString = () => `.${CustomButtom.className}`

export type NavigationButtonProps = Omit<
  FloraButtonProps,
  'shape' | 'isFull' | 'isUppercase' | 'leftIcon' | 'rightIcon' | 'size'
>

export const NextButton = forwardRef<'button', NavigationButtonProps>(
  ({ hierarchy = 'secondary', onClick, disabled, ...props }, forwardedRef) => {
    const {
      state: { goToNext, onNextPress, currentIndex, pages, infinite },
    } = useContext(CarouselContext)

    const handleOnClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        goToNext()
        onNextPress()
        onClick?.(event)
      },
      [goToNext, onClick, onNextPress],
    )

    const isLastPage = useMemo(() => {
      return !!disabled || (!infinite && currentIndex + 1 === pages)
    }, [currentIndex, pages, infinite, disabled])

    return (
      <CustomButtom
        {...props}
        hierarchy={hierarchy}
        onClick={handleOnClick}
        disabled={isLastPage}
        ref={forwardedRef}
        data-to="next"
      >
        <CustomText>próximo</CustomText>
        <ChevronRightIcon />
      </CustomButtom>
    )
  },
)

export const PreviousButton = forwardRef<'button', NavigationButtonProps>(
  ({ hierarchy = 'secondary', onClick, disabled, ...props }, forwardedRef) => {
    const {
      state: { goToPrevious, onPreviousPress, currentIndex, infinite },
    } = useContext(CarouselContext)

    const isFirstPage = useMemo(() => {
      return !!disabled || (!infinite && !currentIndex)
    }, [currentIndex, infinite, disabled])

    const handleOnClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        goToPrevious()
        onPreviousPress()
        if (onClick) {
          onClick(event)
        }
      },
      [goToPrevious, onClick, onPreviousPress],
    )

    return (
      <CustomButtom
        {...props}
        hierarchy={hierarchy}
        onClick={handleOnClick}
        disabled={isFirstPage}
        ref={forwardedRef}
        data-to="previous"
      >
        <ChevronLeftIcon />
        <CustomText>anterior</CustomText>
      </CustomButtom>
    )
  },
)
